// --- Imports ---

import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Point,
    TaskRoute,
    TaskStatus,
    TaskUpdateMarketInfo,
} from "tm-shared/types";

import {
    CommentSelectModal,
    useCheckCommentOnReject,
    useSubtaskConfigSelector,
    useTaskConfigSelector,
} from "@/domains/tasks";
import { SVScanButton } from "@/features/SupervisorScan";
import { useAppDispatch, useTaskPayloadSelector } from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout";
import { Meta } from "@/shared/seo";
import { BottomSheet } from "@/shared/ui";
import {
    mutateUpdateMarketInfoPhotos,
    patchUpdateMarketInfoEntity,
} from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";
import { isSubtaskCompleted } from "@/utils/business/isSubtaskCompleted";

import { BottomSheetContent } from "./components/BottomSheetContent";
import { StepButton } from "./components/StepButton";
import { StepsSection } from "./components/StepsSection";
import { SubtaskRow } from "./components/SubtaskRow";

// --- Types ---

interface UpdateMarketInfoPageProps {
    task: TaskUpdateMarketInfo;
    point: Point;
}

export const UpdateMarketInfoPage: FC<UpdateMarketInfoPageProps> = ({
    task,
    point,
}) => {
    // --- App hooks ---

    const dispatch = useAppDispatch();

    // --- Local state ---

    const [modal, setModal] = useState<"comment" | null>(null);

    // --- Derived identifiers ---

    const taskId = task.id;
    const pointId = point.id;

    // --- Task data ---

    const payloadMap = useTaskPayloadSelector(
        TaskRoute.UpdateMarketInfo,
        taskId,
    );

    const { activeItem: step } = useTaskConfigSelector({
        pointId,
        taskId,
    });

    // --- Memoized values ---

    const currentStep = useMemo(() => {
        const value = step ?? "0";

        return isNaN(+value) ? 0 : +value;
    }, [step]);

    const subtasks = useMemo(() => {
        return task?.context?.subtasks ?? [];
    }, [task?.context?.subtasks]);

    const currentSubtask = useMemo(() => {
        return subtasks[currentStep];
    }, [subtasks, currentStep]);

    const subtaskId = currentSubtask?.subtaskId ?? "";
    const contractId = currentSubtask?.contractId ?? "";

    const { activeItem } = useSubtaskConfigSelector({
        pointId,
        taskId,
        subtaskId,
    });

    const activePayload =
        activeItem != null ? payloadMap?.[activeItem] : undefined;
    const activeStatus = activePayload?.status ?? TaskStatus.Incomplete;
    const savedPhotos = activePayload?.photos ?? [];

    const subtaskTitle = currentSubtask?.name || "Оновлення інформації про ТТ";

    // --- Refs ---

    const prevActiveItemRef = useRef<string | null>(null);

    // --- Domain hooks ---

    const { validateAndNotify } = useCheckCommentOnReject();

    // --- Handlers ---

    const resetStatusIfNeeded = useCallback(
        (itemId: string) => {
            const payload = payloadMap?.[itemId];

            if (!payload) return;

            if (payload.status === TaskStatus.Rejected && !payload.comment) {
                dispatch(
                    patchUpdateMarketInfoEntity({
                        taskId,
                        contractId,
                        subtaskId,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [taskId, contractId, subtaskId, payloadMap, dispatch],
    );

    const onCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                taskId,
                pointId,
                subtaskId,
                field: "activeItem",
                value: null,
            }),
        );
    }, [dispatch, taskId, pointId, subtaskId]);

    const handleStepChange = useCallback(
        (stepKey: number) => {
            dispatch(
                setMdTasksConfig({
                    pointId,
                    taskId,
                    field: "activeItem",
                    value: String(stepKey),
                }),
            );
        },
        [dispatch, taskId, pointId],
    );

    const handleChangeActive = useCallback(
        (id: string) => {
            if (id === activeItem) return;

            const currentId = prevActiveItemRef.current ?? "";
            const currentPayload = payloadMap?.[currentId];

            if (validateAndNotify(currentPayload)) {
                return;
            }

            dispatch(
                setMdTasksConfig({
                    taskId,
                    pointId,
                    subtaskId,
                    field: "activeItem",
                    value: id,
                }),
            );
        },
        [
            dispatch,
            pointId,
            taskId,
            subtaskId,
            activeItem,
            payloadMap,
            validateAndNotify,
        ],
    );

    const handleChangeComment = useCallback(
        (newComment: string) => {
            dispatch(
                patchUpdateMarketInfoEntity({
                    taskId,
                    subtaskId,
                    contractId,
                    patch: { comment: newComment },
                }),
            );
        },
        [dispatch, taskId, subtaskId, contractId],
    );

    const onChangeTaskStatus = useCallback(
        (value: TaskStatus, id?: string) => {
            const target = activeItem ?? id;

            if (!target) return;

            dispatch(
                patchUpdateMarketInfoEntity({
                    taskId,
                    contractId,
                    subtaskId: target,
                    patch: { status: value, comment: "" },
                }),
            );
        },
        [dispatch, taskId, contractId, activeItem],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            if (!activeItem) return;

            dispatch(
                mutateUpdateMarketInfoPhotos({
                    taskId,
                    subtaskId: activeItem,
                    contractId,
                    option: "add",
                    photo,
                }),
            );
        },
        [dispatch, taskId, contractId, activeItem],
    );

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            if (!activeItem) return;

            dispatch(
                mutateUpdateMarketInfoPhotos({
                    taskId,
                    subtaskId: activeItem,
                    contractId,
                    option: "remove",
                    photoId,
                }),
            );
        },
        [dispatch, taskId, contractId, activeItem],
    );

    // --- Effects ---

    useEffect(() => {
        const prev = prevActiveItemRef.current;

        if (prev && activeItem !== prev) {
            resetStatusIfNeeded(prev);
        }

        prevActiveItemRef.current = activeItem ?? null;
    }, [activeItem, resetStatusIfNeeded]);

    // --- Render ---

    return (
        <>
            <Meta title="Market info" />

            <Header
                title={task.name}
                back
                rightActions={
                    <>
                        <SVScanButton pointId={pointId} taskId={taskId} />
                    </>
                }
            />

            <MainContainer $padding="0">
                <StepsSection
                    subtasks={subtasks}
                    currentStep={currentStep}
                    onStepChange={handleStepChange}
                    payloadMapBySubtaskId={payloadMap}
                />

                {currentSubtask && (
                    <SubtaskRow
                        subtask={currentSubtask}
                        isActive={subtaskId === activeItem}
                        onChangeActive={handleChangeActive}
                        onChangeTaskStatus={onChangeTaskStatus}
                        payload={payloadMap?.[subtaskId]}
                    />
                )}
                <StepButton
                    task={task}
                    pointId={pointId}
                    currentCompleted={isSubtaskCompleted(
                        payloadMap?.[subtaskId],
                    )}
                    currentStep={currentStep}
                />
            </MainContainer>

            <BottomSheet open={!!activeItem} onClose={onCloseBottomSheet}>
                <BottomSheetContent
                    title={subtaskTitle}
                    activeStatus={activeStatus}
                    savedPhotos={savedPhotos}
                    activeItemId={activeItem ?? ""}
                    pointId={pointId}
                    captionInfo={{
                        point,
                        taskName: task.name,
                    }}
                    onChangeTaskStatus={onChangeTaskStatus}
                    onUploadPhoto={onUploadPhoto}
                    onDeletePhoto={onDeletePhoto}
                    onOpenCommentModal={() => setModal("comment")}
                />
            </BottomSheet>

            {modal === "comment" && (
                <CommentSelectModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={handleChangeComment}
                    initialComment={activePayload?.comment ?? ""}
                />
            )}
        </>
    );
};
